import NoListings from "./component/NoListings";
import { Box, Card, Chip, CircularProgress, Modal } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import FaiTypography from "MuiComponents/FaiTypography";
import FaiButton from "MuiComponents/FaiButton";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel } from "@mui/x-data-grid";
import theme from "assets/theme";
import Axios from "services/api/config";
import { getSaleURL } from "services/api/routes/Sale";
import CreateSale from "./component/CreateSale";

type ChipColor = "default" | "success" | "error" | "warning" | "info";

const statusColor = (status?: string): ChipColor => {
	const s = (status || "").toUpperCase();
	if (s === "ACTIVE") return "success";
	if (s === "DELETED" || s === "EXPIRED") return "error";
	if (s === "PENDING") return "warning";
	return "info";
};

const columns: any[] = [
	{ headerName: "Campaign", field: "campaignName" },
	{ headerName: "Brand", field: "brand" },
	{ headerName: "Type", field: "saleType" },
	{ headerName: "Discount", field: "discount" },
	{ headerName: "Categories", field: "categories" },
	{ headerName: "Description", field: "description" },
	{ headerName: "Valid From", field: "validFrom" },
	{ headerName: "Valid Till", field: "validTill" },
	{
		headerName: "Status",
		field: "status",
		renderCell: ({ row }: any) => (
			<Chip label={row.status || "-"} color={statusColor(row.status)} size="small" />
		),
	},
];

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : "-");

// Flatten a backend Sale into a flat row.
const names = (arr: any) =>
	Array.isArray(arr) ? arr.map((x: any) => x?.name).filter(Boolean).join(", ") : "";

const toRow = (s: any) => ({
	id: s?.id,
	campaignName: s?.campaignName || "-",
	brand: s?.brand || "-",
	saleType: s?.saleType || "-",
	discount:
		s?.saleType === "FLAT"
			? `₹ ${s?.percentageFlat ?? 0}`
			: `${s?.percentageFlat ?? 0}%`,
	categories:
		names(s?.masterCategories) || names(s?.categories) || names(s?.products) || "-",
	description: s?.description || "-",
	validFrom: fmtDate(s?.validFromDateTime),
	validTill: fmtDate(s?.validTillDateTime),
	status: s?.status || "-",
	raw: s,
});

function Sales() {
	const { typography } = theme;
	const [sales, setSales] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [createModal, setCreateModal] = useState(false);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	const fetchSales = async () => {
		try {
			setLoading(true);
			const query = `page=${paginationModel.page}&size=${paginationModel.pageSize}`;
			const { data } = await Axios({ ...getSaleURL(query) });
			const responseData = data?.data || {};
			setSales((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error("Error fetching sales:", error);
			setSales([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSales();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize]);

	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return sales;
		return sales.filter(
			(r) =>
				r.campaignName.toLowerCase().includes(q) ||
				r.brand.toLowerCase().includes(q)
		);
	}, [sales, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	const closeAndRefresh = () => {
		setCreateModal(false);
		fetchSales();
	};

	return (
		<Card className="my-3 p-3 md:p-5">
			<Box className="flex items-center justify-between">
				<FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
					Sales
				</FaiTypography>
				<FaiButton color="primary" onClick={() => setCreateModal(true)}>
					Create Sale
				</FaiButton>
			</Box>
			<p className="text-sm mb-4 mt-1">
				Create and manage platform-wide sales. Each sale applies a percentage or
				flat discount to a brand's products in the chosen category.
			</p>

			{loading ? (
				<Box className="flex justify-center p-6">
					<CircularProgress />
				</Box>
			) : sales.length === 0 ? (
				<NoListings onAddNew={() => setCreateModal(true)} />
			) : (
				<>
					<Box className="flex flex-col md:flex-row justify-between items-center my-4 w-full gap-3">
						<SearchInput
							placeholder="Search By Campaign / Brand"
							value={search}
							onChange={(e: any) => setSearch(e.target.value)}
						/>
					</Box>
					<Box sx={{ mt: 2 }}>
						<FaiDataGrid
							rows={visibleRows}
							columns={columns}
							pagination={true}
							pageSize={paginationModel.pageSize}
							rowCount={rowCount}
							paginationMode="server"
							onPaginationModelChange={handlePaginationModelChange}
						/>
					</Box>
				</>
			)}

			<Modal
				open={!!createModal}
				onClose={() => setCreateModal(false)}
				aria-labelledby="create-sale-modal"
			>
				<CreateSale onClose={closeAndRefresh} />
			</Modal>
		</Card>
	);
}

export default Sales;
